import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FileText, FileDown, BookOpen, Users, RefreshCw, BookCheck, AlertCircle } from 'lucide-react';
import { exportSummaryReportToPDF } from '../../utils/pdfExport';
import { apiClient } from '../../utils/apiHelper';
import Pagination from '@/components/Pagination';

const LibraryReport = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collegeInfo, setCollegeInfo] = useState(null);
  
  // Pagination State for issues list
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalIssues, setTotalIssues] = useState(0);

  useEffect(() => {
    fetchReport();
    apiClient.get('/api/tenants/my-college/settings').then(r => setCollegeInfo(r.data)).catch(() => {});
  }, [currentPage, itemsPerPage]);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      
      const [booksRes, issuesRes] = await Promise.all([
        apiClient.get('/api/library/books?limit=1000'),
        apiClient.get(`/api/library/issues?${params.toString()}`)
      ]);
      
      // Handle both array and paginated response for books
      let books = [];
      if (Array.isArray(booksRes.data)) {
        books = booksRes.data;
      } else if (booksRes.data?.data) {
        books = Array.isArray(booksRes.data.data) ? booksRes.data.data : [];
      }
      
      // Handle issues response
      let issues = [];
      let totalIssuesCount = 0;
      if (Array.isArray(issuesRes.data)) {
        issues = issuesRes.data;
        totalIssuesCount = issues.length;
      } else {
        issues = issuesRes.data?.data || [];
        totalIssuesCount = issuesRes.data?.total || issues.length;
      }
      
      setTotalIssues(totalIssuesCount);
      
      const currentlyIssued = issues.filter(i => i.status === 'Issued' || !i.return_date);
      const returnedIssues = issues.filter(i => i.status === 'Returned' || i.return_date);
      
      setReport({
        totalBooks: books.length || 0,
        totalIssued: currentlyIssued.length || 0,
        totalReturned: returnedIssues.length || 0,
        availableBooks: books.reduce((sum, b) => sum + (b.available_copies || 0), 0),
        books: books || [],
        recentIssues: currentlyIssued || [],
        recentReturns: returnedIssues.slice(0, 10) || [],
        allIssues: issues || []
      });
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load library data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const exportToPDF = () => {
    if (!report) return;
    
    const stats = {
      'Total Books': report.totalBooks,
      'Currently Issued': report.totalIssued,
      'Total Returned': report.totalReturned,
      'Available Copies': report.availableBooks
    };

    const columns = [
      { header: 'Book Title', dataKey: 'title' },
      { header: 'Author', dataKey: 'author' },
      { header: 'Total Copies', dataKey: 'total_copies' },
      { header: 'Available', dataKey: 'available' }
    ];

    const data = (report.books || []).map(b => ({
      title: b.title || 'N/A',
      author: b.author_name || 'N/A',
      total_copies: b.total_copies || 0,
      available: b.available_copies || 0
    }));

    exportSummaryReportToPDF('Library Report', stats, columns, data, 'library_report.pdf', collegeInfo);
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            Library Report
          </CardTitle>
          <div className="flex gap-2">
            <Button onClick={fetchReport} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
            <Button onClick={exportToPDF} className="bg-green-600 hover:bg-green-700" disabled={!report || report.books?.length === 0}>
              <FileDown className="w-4 h-4 mr-2" />Export PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={fetchReport} className="ml-auto">Retry</Button>
            </div>
          )}

          {report && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600">Total Books</p>
                      <p className="text-2xl font-bold text-blue-700">{report.totalBooks}</p>
                    </div>
                    <BookOpen className="w-8 h-8 text-blue-400" />
                  </div>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-yellow-600">Currently Issued</p>
                      <p className="text-2xl font-bold text-yellow-700">{report.totalIssued}</p>
                    </div>
                    <Users className="w-8 h-8 text-yellow-400" />
                  </div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600">Total Returned</p>
                      <p className="text-2xl font-bold text-green-700">{report.totalReturned}</p>
                    </div>
                    <BookCheck className="w-8 h-8 text-green-400" />
                  </div>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600">Available Copies</p>
                      <p className="text-2xl font-bold text-purple-700">{report.availableBooks}</p>
                    </div>
                    <FileText className="w-8 h-8 text-purple-400" />
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    Book Issue History
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3">Book Title</th>
                          <th className="text-left p-3">Member Name</th>
                          <th className="text-left p-3">Card Number</th>
                          <th className="text-left p-3">Type</th>
                          <th className="text-left p-3">Issue Date</th>
                          <th className="text-left p-3">Due Date</th>
                          <th className="text-left p-3">Return Date</th>
                          <th className="text-center p-3">Status</th>
                          <th className="text-right p-3">Fine</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.allIssues.length === 0 ? (
                          <tr><td colSpan="9" className="text-center p-8 text-gray-500">No book issues recorded</td></tr>
                        ) : (
                          report.allIssues.map((issue, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="p-3 font-medium">{issue.book_title || 'N/A'}</td>
                              <td className="p-3">{issue.member_name || issue.student_name || 'N/A'}</td>
                              <td className="p-3 text-sm text-gray-600">{issue.member_card_number || 'N/A'}</td>
                              <td className="p-3">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  issue.member_type === 'Student' ? 'bg-blue-100 text-blue-800' :
                                  issue.member_type === 'Staff' ? 'bg-purple-100 text-purple-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {issue.member_type || 'N/A'}
                                </span>
                              </td>
                              <td className="p-3">{issue.issue_date || 'N/A'}</td>
                              <td className="p-3">{issue.due_date || 'N/A'}</td>
                              <td className="p-3">{issue.return_date || '-'}</td>
                              <td className="p-3 text-center">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  issue.status === 'Returned' || issue.return_date ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {issue.status || (issue.return_date ? 'Returned' : 'Issued')}
                                </span>
                              </td>
                              <td className="p-3 text-right">
                                {issue.fine_amount > 0 ? <span className="text-red-600 font-medium">₹{issue.fine_amount}</span> : <span className="text-gray-400">-</span>}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    
                    {/* Pagination for Issues */}
                    <Pagination
                      currentPage={currentPage}
                      totalPages={Math.ceil(totalIssues / itemsPerPage)}
                      totalItems={totalIssues}
                      itemsPerPage={itemsPerPage}
                      onPageChange={handlePageChange}
                      onItemsPerPageChange={handleItemsPerPageChange}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LibraryReport;
